import React, { useState } from 'react';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import AppsIcon from '@mui/icons-material/Apps';
import { MainListItems, SecondaryListItems } from './menu-aside';
import MuiDrawer from '@mui/material/Drawer';
import { Avatar, Box, ListSubheader, MenuItem, Popover, MenuList } from '@mui/material';
import { DefaultDialogProps } from './default-dialog';
import ApplicationAppService from '../application/platform/application-app-service';
import AuthenticatorAppService from '../application/platform/security/authenticator-app-service';
import { useBaseLayoutController } from './context/base-layout-controller';
import { useNavigate } from 'react-router-dom';
import { useInactivityTimer } from '../hooks/use-inactivity-timer';
import InactivityWarningModal from './inactivity-warning-modal';

const drawerWidth = 270;

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}
export default function BaseInternalForm({ children }: { children: React.ReactNode }) {
    const authenticationData = ApplicationAppService.getAuthenticationData();
    const applicationData = ApplicationAppService.getApplicationData();
    const [open, setOpen] = useState(true);
    const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
    const [menuPosition, setMenuPosition] = React.useState<{ top: number; left: number } | null>(null);
    const [showWarning, setShowWarning] = useState(false);
    const { showDialog } = useBaseLayoutController();
    const navigate = useNavigate();
    const isLocalhost = window.location.hostname.toLowerCase().includes('localhost');

    // Função de logout por inatividade
    const handleLogoutDueToInactivity = async () => {
        try {
            await AuthenticatorAppService.logout();
        } catch (error) {
            // Mesmo com erro, fazer logout local
            console.error('Erro ao notificar backend sobre logout:', error);
        } finally {
            ApplicationAppService.clearAuthenticationData();
            navigate('/?action=logout');
        }
    };

    // Função para continuar sessão
    const handleContinueSession = () => {
        setShowWarning(false);
        resetTimer();
    };

    // Hook de timer de inatividade (desabilitado em localhost)
    const { remainingTime, resetTimer } = useInactivityTimer({
        totalTimeout: 300000,  // 5 minutos
        warningTimeout: 60000, // 1 minuto
        onWarning: () => { if (!isLocalhost) setShowWarning(true); },
        onTimeout: () => { if (!isLocalhost) handleLogoutDueToInactivity(); },
        events: ['mousemove']
    });

    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
        const element = event.currentTarget;
        const rect = element.getBoundingClientRect();

        // Calcula a posição do menu: abaixo e alinhado à direita do avatar
        setMenuPosition({
            top: rect.bottom + 8, // 8px abaixo do avatar
            left: rect.right - 180, // Alinhado à direita (180 = minWidth do menu)
        });
        setAnchorElUser(element);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
        setMenuPosition(null);
    };

    const toggleDrawer = () => {
        setOpen(!open);
    };

    const Copyright = (props: any) => {
        return (
            <Typography variant="body2" color="text.secondary" align="center" {...props}>
                {'Copyright © '}{' '}
                {new Date().getFullYear()}
                {'.'}
            </Typography>
        );
    }

    const AppBar = styled(MuiAppBar, {
        shouldForwardProp: (prop) => prop !== 'open',
    })<AppBarProps>(({ theme, open }) => ({
        zIndex: theme.zIndex.drawer + 1,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        ...(open && {
            marginLeft: drawerWidth,
            width: `calc(100% - ${drawerWidth}px)`,
            transition: theme.transitions.create(['width', 'margin'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
        }),
    }));

    const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
        ({ theme, open }) => ({
            '& .MuiDrawer-paper': {
                position: 'relative',
                whiteSpace: 'nowrap',
                borderRadius: 0,
                width: drawerWidth,
                transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                }),
                boxSizing: 'border-box',
                ...(!open && {
                    overflowX: 'hidden',
                    transition: theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                    width: theme.spacing(9),
                    [theme.breakpoints.up('sm')]: {
                        width: theme.spacing(9),
                    },
                }),
            },
        }),
    );

    function stringToColor(string: string) {
        let hash = 0;
        let i;

        /* eslint-disable no-bitwise */
        for (i = 0; i < string.length; i += 1) {
            hash = string.charCodeAt(i) + ((hash << 5) - hash);
        }

        let color = '#';

        for (i = 0; i < 3; i += 1) {
            const value = (hash >> (i * 8)) & 0xff;
            color += `00${value.toString(16)}`.slice(-2);
        }
        /* eslint-enable no-bitwise */

        return color;
    }

    function stringAvatar(name: string) {
        return {
            sx: {
                bgcolor: stringToColor(name),
            },
            children: `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`,
        };
    }

    const logout = () => {
        showDialog({
            open: true,
            message: "Deseja realmente sair da aplicação?",
            onYesClick: () => {
                ApplicationAppService.clearAuthenticationData();
                navigate('/?action=logout');
            }
        } as DefaultDialogProps);
    }

    return (
        <Box sx={{ display: 'flex', position: 'relative', width: '100vw', height: '100vh' }}>
            <InactivityWarningModal
                open={showWarning}
                remainingSeconds={remainingTime}
                onContinue={handleContinueSession}
            />
            <AppBar
                position="absolute"
                open={open}
                elevation={0}
                sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 0,
                }}
            >
                <Toolbar
                    sx={{
                        px: 3,
                        minHeight: { xs: 80, md: 107 },
                    }}
                >
                    <IconButton
                        edge="start"
                        color="inherit"
                        aria-label="open drawer"
                        onClick={toggleDrawer}
                        sx={{
                            mr: 2,
                        }}
                    >
                        {open ? <ChevronLeftIcon /> : <MenuIcon />}
                    </IconButton>

                    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box>
                            <Typography
                                component="h1"
                                sx={{
                                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                                    fontWeight: 700,
                                    color: 'inherit',
                                    letterSpacing: '-0.01em',
                                }}
                            >
                                {authenticationData?.type === 1 ? "Entidade" : "Empresa"}: {authenticationData?.company.name}
                            </Typography>
                        </Box>
                    </Box>

                    {authenticationData && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                                <Typography
                                    sx={{
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                        color: 'inherit',
                                        lineHeight: 1.2
                                    }}
                                >
                                    {authenticationData.user.name}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '0.75rem',
                                        color: 'rgba(255, 255, 255, 0.7)',
                                    }}
                                >
                                    {authenticationData.user.email}
                                </Typography>
                            </Box>
                            <IconButton
                                onClick={handleOpenUserMenu}
                                sx={{ p: 0 }}
                                id="btn-avatar"
                            >
                                <Avatar
                                    {...stringAvatar(authenticationData.user.name)}
                                    sx={{
                                        width: 40,
                                        height: 40,
                                    }}
                                />
                            </IconButton>
                            <Popover
                                id="menu-appbar"
                                open={Boolean(anchorElUser) && menuPosition !== null}
                                onClose={handleCloseUserMenu}
                                anchorReference="anchorPosition"
                                anchorPosition={menuPosition ? { top: menuPosition.top, left: menuPosition.left } : undefined}
                                disableScrollLock
                                PaperProps={{
                                    elevation: 3,
                                    sx: {
                                        minWidth: 180,
                                    }
                                }}
                                sx={{
                                    zIndex: (theme) => theme.zIndex.modal + 1
                                }}
                            >
                                <MenuList>
                        <MenuItem
                            onClick={() => {
                                handleCloseUserMenu();
                                navigate('/dashboard');
                            }}
                            sx={{
                                py: 1.5,
                                px: 2,
                            }}
                        >
                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                                Dashboard
                            </Typography>
                        </MenuItem>
                        <MenuItem
                            onClick={handleCloseUserMenu}
                            sx={{
                                py: 1.5,
                                px: 2,
                            }}
                        >
                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                                Perfil
                            </Typography>
                        </MenuItem>
                        <Divider sx={{ my: 0.5 }} />
                        <MenuItem
                            onClick={() => {
                                handleCloseUserMenu();
                                logout();
                            }}
                            sx={{
                                py: 1.5,
                                px: 2,
                            }}
                        >
                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                                Sair
                            </Typography>
                        </MenuItem>
                                </MenuList>
                    </Popover>
                        </Box>
                    )}
                </Toolbar>
            </AppBar>
            <Drawer variant="permanent"
                open={open}
                sx={{
                    borderRadius: 0
                }}>
                <Toolbar
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: open ? 'flex-start' : 'center',
                        justifyContent: 'center',
                        px: 2,
                        py: 2.5,
                        borderRadius: 0,
                        minHeight: { xs: 64, md: 100 },
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                    }}
                >
                    {open ? (
                        <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                                <Box
                                    sx={{
                                        width: 42,
                                        height: 42,
                                        borderRadius: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'primary.main',
                                        color: 'primary.contrastText',
                                    }}
                                >
                                    <AppsIcon sx={{ fontSize: '2.5rem', color: 'primary.contrastText' }} />
                                </Box>
                                <Typography
                                    variant="h1"
                                    sx={{
                                        fontWeight: 700,
                                        color: 'primary.contrastText',
                                        fontSize: '2.5rem',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {applicationData.moduleName}
                                </Typography>

                            </Box>
                            <Typography
                                sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 400,
                                    color: 'rgba(255, 255, 255, 0.8)',
                                    lineHeight: 1.3,
                                    mt: 0.5,
                                    px: 0.5,
                                }}
                            >
                                {applicationData.moduleDescription}
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                width: 55,
                                height: 48,
                                borderRadius: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '1.75rem',
                                    fontWeight: 700,
                                }}
                            >
                                {applicationData.moduleName.charAt(0)}
                            </Typography>
                        </Box>
                    )}
                </Toolbar>
                <List
                    component="nav"
                    sx={{
                        px: 0.5,
                        py: 2,
                    }}
                >
                    <MainListItems open={open} />
                    <Divider sx={{ my: 2, mx: 2 }} />
                    {open && (
                        <ListSubheader
                            component="div"
                            sx={{
                                backgroundColor: 'transparent',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: 'text.secondary',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                px: 2.5,
                                py: 1,
                            }}
                        >
                            Outras Opções
                        </ListSubheader>
                    )}
                    <SecondaryListItems logout={logout} open={open} />
                </List>
            </Drawer>
            <Box
                component="main"
                sx={{
                    backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                            ? theme.palette.grey[100]
                            : theme.palette.grey[900],
                    flexGrow: 1,
                    flexShrink: 1,
                    flexBasis: 0,
                    minHeight: '100vh',
                    minWidth: 0,
                    overflow: 'auto'
                }}
            >
                <Toolbar />
                <Box sx={{ mt: 7, mb: 4, px: 2, width: '100%', boxSizing: 'border-box' }}>
                    <Grid container spacing={2} sx={{ width: 'calc(100% + 16px)', ml: '-8px' }} translate="no">
                        {children}
                    </Grid>

                    <Copyright sx={{
                        pt: 4,

                    }} />
                </Box>
            </Box>
        </Box>
    );
}
